import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import type { ClientSettings } from '@/models/client-settings';
import { FuelPortalService } from '@/services';
import {
  billingServiceAxios,
  destinationsServiceAxios,
  externalNotificationServiceAxios,
  feedIngestServiceAxios,
  fuelMapperServiceAxios,
  fuelToolsApiAxios,
  transcodeJobManagerAxios,
} from '@/services/interceptor';

export const GlobalDataContext = createContext({
  globalData: null as ClientSettings,
});
export const useGlobalData = () => useContext(GlobalDataContext);

type Props = {
  children: React.ReactNode;
};

const GlobalDataProvider = ({ children }: Props) => {
  const [loading, setLoading] = useState(true);
  const [globalData, setGlobalData] = useState<ClientSettings | null>(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const settings = await FuelPortalService.getClientSettings();
        setGlobalData(settings);

        // set base URLs for axios instances
        transcodeJobManagerAxios.defaults.baseURL = settings.transcodeJobManagerUrl;
        feedIngestServiceAxios.defaults.baseURL = settings.feedIngestServicetUrl;
        fuelMapperServiceAxios.defaults.baseURL = settings.fuelMapperServiceUrl;
        billingServiceAxios.defaults.baseURL = settings.billingServiceUrl;
        fuelToolsApiAxios.defaults.baseURL = settings.fuelToolsUrl;
        destinationsServiceAxios.defaults.baseURL = settings.destinationsServiceUrl;
        externalNotificationServiceAxios.defaults.baseURL = settings.externalNotificationServiceUrl;
      } finally {
        setLoading(false);
      }
    };

    fetch();
  }, []);

  const value = useMemo(
    () => ({
      globalData,
    }),
    [globalData]
  );

  if (loading) {
    return null;
  }

  return <GlobalDataContext.Provider value={value}>{children}</GlobalDataContext.Provider>;
};

export default GlobalDataProvider;
